import { useEffect, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { MtxButton, MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useGetApiAccountKycStatus, useGetApiPlacementEligible } from '@/api';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import CardIbanGestionPilotee from '@/components/CardIbanGestionPilotee/CardIbanGestionPilotee';
import CardInformation from '@/components/CardInformation/CardInformation';
import ChipsRadio from '@/components/chipsRadio/chipsRadio';
import ContractTutorials from '@/components/ContractTutorials';
import FormTextError from '@/components/formTextError/formTextError';
import { Loader } from '@/components/Loader/loader';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import { NFD_DIACRITICS } from '@/constants/regex';
import { ROUTE } from '@/constants/route';
import { CONTRACT_FAMILY } from '@/enums/contractFamily';
import { STATUS_KYC } from '@/enums/statusKYC';
import { MAJOR_ADULT_LEGAL_CAPACITY } from '@/enums/userLegalCapacity';
import { USER_TYPE } from '@/enums/userType';
import { useAuth } from '@/hooks/useAuth';
import useContractTutorials from '@/hooks/useContractTutorials';
import { useGestionPilotee } from '@/hooks/useGestionPilotee';
import { useRappelConseiller } from '@/hooks/useRappelConseiller';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { ContentModalEligibiliteContrat } from '@/modals/ContentModalEligibiliteContrat/ContentModalEligibiliteContrat';
import { contentModalEligibiliteContratData } from '@/modals/ContentModalEligibiliteContrat/data';
import ContentModalKyc from '@/modals/ContentModalKyc/ContentModalKyc';
import { LOCATION_KEYS } from '@/modals/ContentModalKyc/dico';
import { ModalRappel } from '@/modals/ContentModalRappel/ContentModalRappel';
import { useGetGestionsPilotees } from '@/services/getGestionsPilotees/getGestionsPilotees';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';
import { useUserStore } from '@/store/useStore';
import { GestionPilotee, PiloteManagementResponse, VersementData } from '@/types/parcoursVersementTypes';
import joinClassNames from '@/utils/functions/joinClassNames/joinClassNames';
import { log } from '@/utils/functions/logger/logger';

import styles from './styles.module.css';

interface RouteParams {
  ':familleContrat': string;
  ':typeContrat': string;
}

export const SelectOperation: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: kycStatus } = useGetApiAccountKycStatus({
    query: {
      enabled: user?.type === USER_TYPE.client,
    },
  });
  const { state } = useLocation();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const [showLoader, setShowLoader] = useState(false);
  const [title, setTitle] = useState('');
  const [isModalKycOpen, setIsModalKycOpen] = useState(false);

  const [operation, setOperation] = useState<keyof typeof LOCATION_KEYS>('versement');

  let tmpVersementData: Partial<VersementData> = {};
  try {
    const item = sessionStorage.getItem('parcoursVersementData');
    tmpVersementData = item ? JSON.parse(item) : {};
  } catch {
    tmpVersementData = {};
  }

  // Object to store in sessionStorage for data persistance
  const versementData: VersementData = (() => {
    let initialData: VersementData = {
      token: '',
      key: state?.key,

      contratFamille: tmpVersementData?.contratFamille ?? state?.infoContrat.famille,
      contratNomProduit: tmpVersementData?.contratNomProduit ?? state?.infoContrat.nomProduit,
      contratPlacementId: tmpVersementData?.contratPlacementId ?? state?.infoContrat.placementId,
      contratGestionnaireAC: tmpVersementData?.contratGestionnaireAC ?? state?.infoContrat.gestionnaireAC,
      contratGestionnaireLogo: tmpVersementData?.contratGestionnaireLogo ?? state?.infoContrat.gestionnaireLogo,
      contratValorisationEncours:
        tmpVersementData?.contratValorisationEncours ?? state?.infoContrat.valorisation.encours,
      contratPerformance: tmpVersementData?.contratPerformance ?? state?.infoContrat.evolution.performance,
      contratPlusMoinsValue: tmpVersementData?.contratPlusMoinsValue ?? state?.infoContrat.evolution.plusMoinsValue,
      contratCancelablePeriod: tmpVersementData?.contratCancelablePeriod ?? state?.infoContrat?.cancelablePeriod,

      gpSelected: {
        codeSupport: '',
        profil: '',
        libelleSupport: '',
        gestionnaire: '',
        srri: 0,
        type: '',
      },
      cbs: {
        '': {
          iban: '',
          mandat_prelevement: false,
          banque: '',
        },
      },
      idSouscription: 0,
      typeVersement: '',
      montantVersement: 0,
      objectifPrincipal: '',
      origineCapitaux: '',
      autreOrigineFonds: '',
      dateOrigineFonds: '',
      placementDesFonds: '',
      ibanSelect: '',
      per: false,
      deductionFiscale: null,
      confirmSituation: false,
      typeIdentite: '',
      'justificatif-identite-1-recto': {
        name: '',
        size: 0,
        symbol: '',
        extension: '',
        url: '',
      },
      'justificatif-identite-1-verso': {
        name: '',
        size: 0,
        symbol: '',
        extension: '',
        url: '',
      },
      'justificatif-telephone': {
        name: '',
        size: 0,
        symbol: '',
        extension: '',
        url: '',
      },
      'justificatif-origine_fonds': {
        name: '',
        size: 0,
        symbol: '',
        extension: '',
        url: '',
      },
    };
    if (sessionStorage.parcoursVersementData) {
      const getVersementData = JSON.parse(sessionStorage.parcoursVersementData);
      initialData = getVersementData;
    }
    return initialData;
  })();

  const familleContrat = versementData?.contratFamille === CONTRACT_FAMILY.ASSURANCEVIE ? 'assurancevie' : 'retraite';
  const userConseiller = useUserStore((state) => state.user);

  const [tokenVersement, setTokenVersement] = useState(versementData?.token);

  const rappel = useRappelConseiller();

  const {
    data: eligibleContrat,
    isPending: eligibleContratPending,
    isLoading: eligibleContratLoading,
    isError: eligibleContratError,
  } = useGetApiPlacementEligible(versementData?.contratPlacementId);

  const { data, isLoading, isPending, isError } = useGetGestionsPilotees(tokenVersement, versementData?.key);
  const GP = useGestionPilotee();

  const [showModalRappel, setShowModalRappel] = useState(false);
  const [showModalEligible, setShowModalEligible] = useState(false);
  const [modalContent, setModalContent] = useState('');

  const codesErreursModal = ['CAPJUR', 'GPOUT', 'PLAOWN', 'RENSPC', 'PRESPC', 'AVNENC'];

  const [gpSelected, setGpSelected] = useState<GestionPilotee>({
    codeSupport: versementData?.gpSelected.codeSupport,
    profil: versementData?.gpSelected.profil,
    libelleSupport: versementData?.gpSelected.libelleSupport,
    gestionnaire: versementData?.gpSelected.gestionnaire,
    srri: versementData?.gpSelected.srri,
    type: versementData?.gpSelected.type,
  });

  const [displayFormError, setDisplayFormError] = useState(false);

  const [gestionPilote, setGestionPilote] = useState(gpSelected.codeSupport === '' ? '' : 'gestionPilote');

  const [objPrincipal, setObjPrincipal] = useState('');

  const [showGPs, setShowGPs] = useState(gpSelected.codeSupport !== '');

  let breadcrumbItems: BreadcrumbItem[];

  const getBreadcrumbLabel = () => {
    if (versementData?.key === 'versement') return 'Choisir un support';
    if (versementData?.key) return 'Effectuer un ' + versementData.key;
    return 'Effectuer une opération';
  };

  const [breadcrumbLabel, setBreadcrumbLabel] = useState(getBreadcrumbLabel());

  if (state?.breadcrumbItems && state?.breadcrumbItems !== undefined) {
    breadcrumbItems = [
      ...state.breadcrumbItems,
      { label: breadcrumbLabel, path: ROUTE.SELECTOPERATION, key: state.key },
    ];
  } else {
    breadcrumbItems = [
      { label: 'Mes opérations', path: ROUTE.OPERATIONS },
      {
        label: 'Choisir un contrat',
        path: ROUTE.SELECTCONTRACT,
        key: versementData?.key,
      },
      { label: breadcrumbLabel, path: ROUTE.SELECTOPERATION, key: versementData?.key },
    ];
  }

  useEffect(() => {
    if (!versementData?.contratFamille) {
      navigate(ROUTE.OPERATIONS);
    }
  }, [versementData]);

  const mapRoute: RouteParams = {
    ':familleContrat':
      versementData?.contratFamille?.toLowerCase() === 'assurancevie'
        ? 'assurance-vie'
        : versementData?.contratFamille?.toLowerCase(),
    ':typeContrat': versementData?.contratNomProduit
      ?.toLowerCase()
      ?.replaceAll(' ', '-')
      ?.normalize('NFD')
      ?.replaceAll(NFD_DIACRITICS, ''),
  };

  const openModalRappel = () => {
    rappel.mutate();
    setShowModalRappel(true);
    setShowModalEligible(false);
  };

  const openModal = (modal: string) => {
    document.body.classList.add('modal-open');

    switch (modal) {
      case 'showModalEligible':
        setShowModalEligible(true);
        break;

      case 'showModalCAPJUR':
        setShowModalEligible(true);
        break;

      case 'showModalGPOUT':
        setShowModalEligible(true);
        break;

      case 'showModalAVNENC':
        setShowModalEligible(true);
        break;

      case 'showModalRappel':
        setShowModalRappel(true);
        break;

      default:
        break;
    }
  };

  const closeModal = () => {
    setShowModalEligible(false);
    setShowModalRappel(false);
  };

  const setVersementData = (data: PiloteManagementResponse) => {
    sessionStorage.setItem(
      'parcoursVersementData',
      JSON.stringify({
        ...versementData,

        token: tokenVersement,

        gpSelected: {
          codeSupport: gpSelected.codeSupport,
          profil: gpSelected.profil,
          libelleSupport: gpSelected.libelleSupport,
          gestionnaire: gpSelected.gestionnaire,
          srri: gpSelected.srri,
          type: gpSelected.type,
        },

        cbs: data.tokenDecode.cbs,

        idSouscription: data.idSouscription,
        typeVersement: '',
        montantVersement: 0,
        objectifPrincipal: '',
        origineCapitaux: '',
        dateOrigineFonds: '',
        placementDesFonds: '',
        autreOrigineFonds: '',
        ibanSelect: '',
        per: data.tokenDecode.retraite,
        deductionFiscale: null,
        confirmSituation: false,
        typeIdentite: '',

        'justificatif-identite-1-recto': {
          name: '',
          size: 0,
          symbol: '',
          extension: '',
          url: '',
        },
        'justificatif-identite-1-verso': {
          name: '',
          size: 0,
          symbol: '',
          extension: '',
          url: '',
        },
        'justificatif-telephone': {
          name: '',
          size: 0,
          symbol: '',
          extension: '',
          url: '',
        },
        'justificatif-origine_fonds': {
          name: '',
          size: 0,
          symbol: '',
          extension: '',
          url: '',
        },
      })
    );
  };

  const handleGPSubmit = () => {
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'gestion pilotee',
      event_detail_1: 'cta',
      event_detail_2: `${versementData?.contratFamille} - ${versementData?.contratNomProduit}`,
    });

    if (!eligibleContrat?.valid) {
      switch (eligibleContrat?.codeErreur) {
        case 'CAPJUR':
          setModalContent('modalCAPJUR');
          openModal('showModalEligible');
          break;

        case 'GPOUT':
          setModalContent('modalGPOUT');
          openModal('showModalEligible');
          break;

        case 'PLAOWN':
          setModalContent('modalPLAOWN');
          openModal('showModalEligible');
          break;

        case 'RENSPC':
          setModalContent('modalRENSPC');
          openModal('showModalEligible');
          break;

        case 'PRESPC':
          setModalContent('modalPRESPC');
          openModal('showModalEligible');
          break;

        case 'AVNENC':
          setModalContent('modalAVNENC');
          openModal('showModalEligible');
          break;
      }
      return;
    }

    if (gpSelected.codeSupport !== '') {
      if (JSON.stringify(versementData?.gpSelected) === JSON.stringify(gpSelected)) {
        navigate(
          ROUTE.DEPOSIT.replaceAll(/:familleContrat|:typeContrat/gi, (matched) => {
            return mapRoute[matched as keyof RouteParams];
          }),
          {
            state: {
              objPrincipal,
              breadcrumbItems,
              gpSelected,
              data,
            },
          }
        );
      }

      setShowLoader(true);

      GP.mutate(gpSelected, {
        onSuccess: (data) => {
          setVersementData(data);

          navigate(
            ROUTE.DEPOSIT.replaceAll(/:familleContrat|:typeContrat/gi, (matched) => {
              return mapRoute[matched as keyof RouteParams];
            }),
            {
              state: {
                data,
                objPrincipal,
                breadcrumbItems,
                gpSelected,
              },
            }
          );
        },
        onError: (error) => {
          navigate(ROUTE.ERREUR);
          log.error(`SelectOperation - handleGPSubmit - GP.mutate error: ${error}`);
        },
      });
    } else {
      setGpSelected({
        codeSupport: '',
        profil: '',
        libelleSupport: '',
        gestionnaire: '',
        srri: 0,
        type: '',
      });
      setDisplayFormError(true);
    }
  };

  useEffect(() => {
    if (data) setObjPrincipal(data?.objectifPrincipal);

    if (showGPs && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [data, showGPs]);

  useEffect(() => {
    if (user?.id && user.type && user.success) {
      switch (versementData?.key) {
        case 'versement':
          if (eligibleContrat?.valid === true) {
            TRACKING_INSTANCE.setTcVars({
              page_nom: 'versement - choisir un support',
              page_template: 'espace client',
              user_id: user.id,
              user_client: user.type === 'Client',
              user_connexion: user.success,
              user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
              tunnel_produit: versementData?.contratNomProduit,
              tunnel_metier: 'espace client placement',
              tunnel_sous_metier: familleContrat,
              tunnel_categorie_produit: 'parcours de versement',
            });
            void TRACKING_INSTANCE.triggerEvent('page', undefined, {
              event_id: 'page',
              event_categorie: 'affichage espace client',
              event_nom: 'versement - choisir un support',
              event_page: 'versement - choisir un support',
            });
          } else if (eligibleContrat?.valid === false) {
            TRACKING_INSTANCE.setTcVars({
              page_nom: 'versement - effectuer un versement',
              page_template: 'espace client',
              user_id: user.id,
              user_client: user.type === 'Client',
              user_connexion: user.success,
              user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
              tunnel_produit: versementData?.contratNomProduit,
              tunnel_metier: 'espace client placement',
              tunnel_sous_metier: familleContrat,
              tunnel_categorie_produit: 'parcours de versement',
            });
            void TRACKING_INSTANCE.triggerEvent('page', undefined, {
              event_id: 'page',
              event_categorie: 'affichage espace client',
              event_nom: 'versement - effectuer un versement',
              event_page: 'versement - effectuer un versement',
            });
          }
          break;

        case 'arbitrage':
          TRACKING_INSTANCE.setTcVars({
            page_nom: 'arbitrage - effectuer un arbitrage',
            page_template: 'espace client',
            user_id: user.id,
            user_client: user.type === 'Client',
            user_connexion: user.success,
            user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
          });
          void TRACKING_INSTANCE.triggerEvent('page', undefined, {
            event_id: 'page',
            event_categorie: 'affichage espace client',
            event_nom: 'arbitrage - effectuer un arbitrage',
            event_page: 'arbitrage - effectuer un arbitrage',
          });
          break;

        case 'rachat/retrait':
          TRACKING_INSTANCE.setTcVars({
            page_nom: 'rachat - effectuer un rachat/retrait',
            page_template: 'espace client',
            user_id: user.id,
            user_client: user.type === 'Client',
            user_connexion: user.success,
            user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
          });
          void TRACKING_INSTANCE.triggerEvent('page', undefined, {
            event_id: 'page',
            event_categorie: 'affichage espace client',
            event_nom: 'rachat - effectuer un rachat/retrait',
            event_page: 'rachat - effectuer un rachat/retrait',
          });
          break;
      }
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [
    eligibleContrat,
    familleContrat,
    versementData?.contratNomProduit,
    versementData?.key,
    user?.id,
    user?.success,
    user?.type,
  ]);

  useEffect(() => {
    if (eligibleContrat?.valid === true || eligibleContrat?.codeErreur) {
      if (!tokenVersement) {
        setTokenVersement(eligibleContrat?.token ?? '');
        sessionStorage.setItem(
          'parcoursVersementData',
          JSON.stringify({
            ...versementData,
            token: eligibleContrat.token,
          })
        );
      }
    }

    if (
      versementData?.key === 'versement' &&
      (eligibleContrat?.valid || codesErreursModal.includes(eligibleContrat?.codeErreur || ''))
    ) {
      setTitle('Choisir un support');
      setBreadcrumbLabel('Choisir un support');

      setOperation('versementlibre');
    } else if (versementData?.key === 'versement') {
      setTitle('Effectuer un versement');
      setBreadcrumbLabel('Effectuer un versement');
      setOperation('versement');
    } else if (versementData?.key === 'arbitrage') {
      setTitle('Effectuer un arbitrage');
      setOperation('arbitrage');
    } else {
      setTitle('Effectuer un rachat/retrait');
      setOperation('rachatRetrait');
    }

    if (document.body.classList.contains('modal-open')) document.body.classList.remove('modal-open');
  }, [eligibleContrat, codesErreursModal, versementData?.key, tokenVersement, versementData]);

  useEffect(() => {
    if (eligibleContrat?.valid === undefined) return;

    datadogRum.addAction('versement_eligibilite_verifiee', {
      valid: eligibleContrat.valid,
      codeErreur: eligibleContrat.codeErreur ?? null,
    });
  }, [eligibleContrat]);

  if (isError || eligibleContratError) navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.DASHBOARD } });

  const handleContinueClick = () => {
    if (kycStatus?.statutKyc === STATUS_KYC.OK) {
      handleGPSubmit();
    } else if (kycStatus && (kycStatus.statutKyc as string) !== STATUS_KYC.OK) {
      if (state.key === 'versement') setOperation('versementlibre');

      void TRACKING_INSTANCE.triggerEvent('action', undefined, {
        event_id: 'action',
        event_categorie:
          user?.capaciteJuridique && user?.capaciteJuridique in MAJOR_ADULT_LEGAL_CAPACITY
            ? 'affichage popin mettre a jour informations'
            : 'affichage popin données personnelles mineur',
        event_nom:
          user?.capaciteJuridique && user?.capaciteJuridique in MAJOR_ADULT_LEGAL_CAPACITY
            ? 'affichage popin mettre a jour informations'
            : 'affichage popin données personnelles mineur',
        event_metier: 'espace client placement',
        event_detail_1: 'cta',
        event_detail_2: `${versementData?.contratFamille} - ${versementData?.contratNomProduit}`,
      });
      setIsModalKycOpen(true);
    }
  };

  const handlePartnerAccess = () => {
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'acceder a mon espace partenaire',
      event_detail_1: 'cta',
      event_detail_2: `${versementData?.contratFamille} - ${versementData?.contratNomProduit}`,
    });
    if (kycStatus?.statutKyc === STATUS_KYC.OK) {
      globalThis.open(versementData?.contratGestionnaireAC, '_blank', 'noopener,noreferrer');
    } else {
      if (state.key === 'versement') setOperation('versement');
      void TRACKING_INSTANCE.triggerEvent('action', undefined, {
        event_id: 'action',
        event_categorie:
          user?.capaciteJuridique && user?.capaciteJuridique in MAJOR_ADULT_LEGAL_CAPACITY
            ? 'affichage popin mettre a jour informations'
            : 'affichage popin données personnelles mineur',
        event_nom:
          user?.capaciteJuridique && user?.capaciteJuridique in MAJOR_ADULT_LEGAL_CAPACITY
            ? 'affichage popin mettre a jour informations'
            : 'affichage popin données personnelles mineur',
        event_metier: 'espace client placement',
        event_detail_1: 'cta',
        event_detail_2: `${versementData?.contratFamille} - ${versementData?.contratNomProduit}`,
      });
      setIsModalKycOpen(true);
    }
  };

  const contractTutorials = useContractTutorials({
    link: versementData?.contratGestionnaireAC,
    operation: versementData?.key,
    onPartnerAccess: handlePartnerAccess,
  });
  const bottomInfoClassName = joinClassNames(
    styles.selectOperationBottomInfo,
    contractTutorials.showOperationTutorial && styles.bottomInfoWithTutorial
  );

  return (
    <MtxGrid className={styles.selectOperation}>
      {(showLoader || (eligibleContratPending && eligibleContratLoading) || (isPending && isLoading)) && <Loader />}

      {showModalRappel && (
        <ModaleDefaut
          content={<ModalRappel onClose={closeModal} data={rappel.data} />}
          onClickClose={closeModal}
          size="large"
          mobileStyle="defaut"
        />
      )}

      {showModalEligible && (
        <ModaleDefaut
          title={contentModalEligibiliteContratData[modalContent].title}
          content={
            <ContentModalEligibiliteContrat
              modal={modalContent}
              openModal={openModalRappel}
              cancelablePeriod={versementData?.contratCancelablePeriod}
            />
          }
          btnLabel={contentModalEligibiliteContratData[modalContent].btnLabel}
          btnVariant="primary"
          btnSize="large"
          onClickBtn={() =>
            contentModalEligibiliteContratData[modalContent].onClickBtn(versementData?.contratGestionnaireAC)
          }
          onClickClose={() => closeModal()}
          mobileStyle="stick-to-bottom"
          size="large"
          infoCircle={true}
        />
      )}

      <MtxGrid className={styles.selectOperationHeadTitleImg} row>
        <MtxGrid col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
          <Breadcrumb items={breadcrumbItems} />
        </MtxGrid>

        <MtxGrid col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
          <h1 className={styles.selectOperationTitle}> {title} </h1>
        </MtxGrid>
      </MtxGrid>

      <div className={styles.selectOperationBody}>
        <div className="">
          <p className={styles.selectOperationSelectSupportTxt}>
            {versementData?.key
              ? t('selectOperation.operationText', {
                  operationType: versementData.key,
                })
              : t('selectOperation.operationTextDefault')}
          </p>
          <MtxGrid row>
            <MtxGrid
              className={styles.boxCardInsuranceCardInformation}
              col={4}
              colXs={12}
              colSm={6}
              colMd={4}
              colLg={4}
              colXl={4}
            >
              <CardInformation
                famille={versementData?.contratFamille}
                interets={0}
                logoAssuranceDefault={versementData?.contratGestionnaireLogo}
                montant={versementData?.contratValorisationEncours}
                nomContrat={versementData?.contratNomProduit}
                performance={versementData?.contratPerformance}
                plusMoinsValue={versementData?.contratPlusMoinsValue}
                isInterets={false}
              />
            </MtxGrid>
          </MtxGrid>
        </div>

        {(eligibleContrat?.valid || codesErreursModal.includes(eligibleContrat?.codeErreur || '')) &&
        versementData?.key === 'versement' ? (
          <>
            <div>
              <p className={styles.selectOperationTxtChips}>{t('selectOperation.selectSupportText')}</p>

              <div className={styles.selectOperationChips}>
                <ChipsRadio
                  size="large"
                  label="Gestion Pilotée"
                  name="parcoursVersement"
                  value={gestionPilote}
                  isChecked={gpSelected.codeSupport === '' ? gestionPilote === 'gestionPilote' : true}
                  onClick={() => {
                    setGestionPilote('gestionPilote');
                    if (data) {
                      setObjPrincipal(data.objectifPrincipal);
                      if (data.gpsDispo.length === 1) {
                        setGpSelected(data.gpsDispo[0]);
                      }
                    }
                    setShowGPs(true);

                    window.scrollTo({
                      top: document.body.scrollHeight,
                      behavior: 'smooth',
                    });
                  }}
                />
                <ChipsRadio
                  size="large"
                  label="Autre support"
                  name="parcoursVersement"
                  value={gestionPilote}
                  onClick={() => {
                    setGestionPilote('autreSupport');
                    setDisplayFormError(false);
                    setGpSelected({
                      codeSupport: '',
                      profil: '',
                      libelleSupport: '',
                      gestionnaire: '',
                      srri: 0,
                      type: '',
                    });
                    setShowGPs(true);
                    window.scrollTo({
                      top: document.body.scrollHeight,
                      behavior: 'smooth',
                    });
                  }}
                />
              </div>
            </div>

            {showGPs && (
              <div
                className={
                  data?.gpsDispo.length > 1 ? styles.selectOperationContainer : styles.selectOperationWithoutCard
                }
                ref={containerRef}
              >
                {gestionPilote === 'gestionPilote' ? (
                  <div>
                    {data?.gpsDispo.length > 1 && (
                      <div>
                        <p className={styles.selectOperationBoxPilotedManagementDescription}>
                          {t('selectOperation.selectGPText')}
                        </p>

                        <MtxGrid className={styles.selectOperationGestionPilotees} row>
                          {data.gpsDispo.map((value: GestionPilotee) => {
                            return (
                              <MtxGrid
                                key={`${value.codeSupport}-${value.profil}-${value.libelleSupport}-${value.gestionnaire}-${value.srri}-${value.type}`}
                                className={styles.selectOperationGestionPiloteesCard}
                                col={4}
                                colXs={12}
                                colSm={6}
                                colMd={6}
                                colLg={4}
                                colXl={4}
                              >
                                <CardIbanGestionPilotee
                                  gestionnaire={value.gestionnaire}
                                  prudence={value.libelleSupport}
                                  risque={value.srri}
                                  type={value.type}
                                  name="choix-gestionPilotee"
                                  isChecked={JSON.stringify(gpSelected) === JSON.stringify(value)}
                                  onChange={() => {
                                    setGpSelected(value);
                                    window.scrollTo({
                                      top: document.body.scrollHeight,
                                      behavior: 'smooth',
                                    });
                                  }}
                                />
                              </MtxGrid>
                            );
                          })}
                        </MtxGrid>

                        {displayFormError && (
                          <div className={styles.selectOperationChoixGpError}>
                            <FormTextError text="Veuillez sélectionner l’une des options proposées" />
                          </div>
                        )}
                      </div>
                    )}

                    <MtxGrid className={styles.selectOperationConfirmBtnBox} row>
                      <MtxGrid col={4} colXs={12} colSm={6} colMd={4} colLg={4} colXl={4}>
                        <MtxButton
                          className={styles.selectOperationConfirmBtn}
                          onClick={handleContinueClick}
                          variant="primary"
                          size="large"
                          disabled={userConseiller?.advisorView === true}
                          fullWidth={true}
                        >
                          <div>{t('common.continue')}</div>
                        </MtxButton>{' '}
                      </MtxGrid>
                    </MtxGrid>
                  </div>
                ) : (
                  <div
                    className={joinClassNames(
                      styles.partnerArea,
                      contractTutorials.showOperationTutorial && styles.partnerAreaWithTutorial
                    )}
                  >
                    <div className={bottomInfoClassName}>
                      <i className="totem-icon totem-icon-info" />

                      <div className={styles.bottomInfoContent}>
                        <p>
                          <Trans
                            i18nKey={
                              contractTutorials.showOperationTutorial
                                ? 'selectOperation.otherSupportWithTutorial'
                                : 'selectOperation.otherSupport'
                            }
                            components={{ span: <strong /> }}
                          />
                        </p>
                        <ContractTutorials state={contractTutorials} />
                      </div>
                    </div>

                    <MtxGrid className={styles.selectOperationConfirmBtnBox} row>
                      <MtxGrid col={4} colXs={12} colSm={6} colMd={4} colLg={4} colXl={4}>
                        <MtxButton
                          className={styles.selectOperationConfirmBtn}
                          onClick={handlePartnerAccess}
                          variant="primary"
                          size="large"
                          disabled={false}
                          fullWidth={true}
                        >
                          <div>{t('partnerInfo.label')}</div>
                        </MtxButton>
                      </MtxGrid>
                    </MtxGrid>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div className={bottomInfoClassName}>
              <i className="totem-icon totem-icon-info" />
              <div className={styles.bottomInfoContent}>
                <p>
                  {t('selectOperation.arbitrationText')}
                  {contractTutorials.showOperationTutorial && <> {t('videoTutorials.exitGuide')}</>}
                </p>
                <ContractTutorials state={contractTutorials} />
              </div>
            </div>

            <MtxGrid className={styles.selectOperationConfirmBtnBox} row>
              <MtxGrid col={4} colXs={12} colSm={6} colMd={6} colLg={4} colXl={4}>
                <MtxButton
                  className={styles.selectOperationConfirmBtn}
                  onClick={handlePartnerAccess}
                  variant="primary"
                  size="large"
                  disabled={false}
                  fullWidth={true}
                >
                  <div>{t('partnerInfo.label')}</div>
                </MtxButton>
              </MtxGrid>
            </MtxGrid>
          </>
        )}
      </div>
      {isModalKycOpen && (
        <ContentModalKyc
          onClickClose={() => setIsModalKycOpen(false)}
          WarningIcon={true}
          legalCapacity={user?.capaciteJuridique}
          variant="tertiary"
          location={operation}
          investmentType={versementData?.contratFamille}
          contractName={versementData?.contratNomProduit}
        />
      )}
    </MtxGrid>
  );
};
